/* =============================================================
   BELLA n' BEAR — Cart Drawer engine (v6, instant-open + optimistic)

   Optimistic add sources:
   - Collection/search quick-add: bb-collection-cards.js reads #bb-cf-data and
     dispatches 'cart:adding' with an optimistic line.
   - PDP main add-to-cart (Horizon 'shopify:cart:lines-update'): the engine
     looks the tapped variant up in window.__bbCartProduct (bb-pdp-cart-data).
   New optimistic lines are prepended (Shopify returns newest-first) and their
   compare-at is cached so the reconcile keeps the savings with no flicker.

   All CSS (including the mobile qty-squish media query) now lives in the cached
   assets/bb-cart-drawer.css — this engine no longer injects any <style>.
============================================================= */
(function () {
  'use strict';

  var entry  = (window.__bbCartDrawers || [])[0] || {};
  var SID    = entry.sid || '';
  var CONFIG = entry.config || {};
  CONFIG.sid = SID;

  if (CONFIG.shippingTiers && CONFIG.shippingTiers.sort) {
    CONFIG.shippingTiers = CONFIG.shippingTiers.slice().sort(function (a, b) {
      return a.threshold - b.threshold;
    });
  }

  var LOW_STOCK = 3;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, CONFIG.moneyFormat);
    }
    return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
  }

  // The main PDP gallery image that's already decoded on screen. Reusing its
  // exact URL for the optimistic line means the drawer paints from cache with no
  // blank frame (the PDP add's "loads in -> grey -> reloads" flash).
  function pdpMainImage() {
    var im = document.querySelector('.product-media__image');
    if (!im || !im.complete || !im.naturalWidth) {
      var imgs = [].slice.call(document.querySelectorAll(
        '[class*="product-media"] img, [class*="media-gallery"] img'
      )).filter(function (x) { return x.complete && x.naturalWidth > 200; });
      imgs.sort(function (a, b) { return b.naturalWidth - a.naturalWidth; });
      im = imgs[0];
    }
    return (im && im.complete && im.naturalWidth > 0) ? (im.currentSrc || im.src) : '';
  }

  function CartDrawer(sid) {
    this.sid          = sid;
    this.isOpen       = false;
    this.panel        = $('#CartDrawer-' + sid);
    this.overlay      = $('#CartDrawerOverlay-' + sid);
    this.closeBtn     = $('#CartDrawerClose-' + sid);
    this.loading      = $('#CartDrawerLoading-' + sid);
    this.itemsList    = $('#CartDrawerItems-' + sid);
    this.subtotal     = $('#CartDrawerSubtotal-' + sid);
    this.savingsTotal = $('#CartDrawerSavings-' + sid);
    this.shippingLine = $('#CartDrawerShippingLine-' + sid);
    this.total        = $('#CartDrawerTotal-' + sid);
    this.empty        = $('#CartDrawerEmpty-' + sid);
    this.footer       = $('#CartDrawerFooter-' + sid);
    this.count        = $('#CartDrawerCount-' + sid);
    this.shippingMsg  = $('#CartDrawerShippingMsg-' + sid);
    this.shippingFill = $('#CartDrawerShippingFill-' + sid);
    this.shippingWrapper = $('.cart-drawer-shipping-' + sid);

    this._capCache = window['__bbCartCap_' + sid] || {};
    this._stockCache = window['__bbCartStock_' + sid] || {};
    // Per-variant image URL cache. The first image we display for a variant is
    // kept and reused on every subsequent render (optimistic -> reconcile -> qty
    // change), so the <img> src never swaps to a different-size URL and the
    // browser never re-downloads it (the "grey flash" / late image fill).
    this._imgCache = {};
    this._lastCart = null;

    this.init();
  }

  CartDrawer.prototype.init = function () {
    var self = this;

    if (!CONFIG.showShippingBar && this.shippingWrapper) {
      this.shippingWrapper.style.display = 'none';
    }

    this.closeBtn.addEventListener('click', function () { self.close(); });
    this.overlay.addEventListener('click', function () { self.close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
    });

    $('#CartDrawerCheckout-' + this.sid).addEventListener('click', function () {
      window.location.href = '/checkout';
    });

    var keepShoppingBtn = $('#CartDrawerKeepShopping-' + this.sid);
    if (keepShoppingBtn) {
      keepShoppingBtn.addEventListener('click', function () { self.close(); });
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest(
        '[data-cart-drawer-trigger],' +
        '[data-testid="cart-drawer-trigger"],' +
        '[aria-controls="cart-drawer"],' +
        'a[href="/cart"],' +
        '.header-actions__cart-icon,' +
        'cart-icon'
      );
      if (!trigger) return;
      var href = trigger.getAttribute && trigger.getAttribute('href');
      if (href && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      self.fetchAndOpen();
    }, true);

    document.addEventListener('shopify:cart:lines-update', function (event) {
      self.onCartLinesUpdate(event);
    });

    document.addEventListener('cart:adding', function (e) {
      if (!self.isOpen) self.open();
      var opt = e && e.detail && e.detail.optimistic;
      if (opt) {
        self.renderOptimistic(opt);
      } else {
        self.setLoading(true);
      }
      // Safety net: whatever the source (collection grid, home/PDP slider), the
      // optimistic line is only a guess built on _lastCart. Always reconcile to
      // the authoritative /cart.js shortly after so the drawer can never be left
      // showing a partial cart (e.g. the PDP slider that only showed the item
      // just added). Debounced so rapid adds coalesce into one reconcile.
      self.scheduleReconcile();
    });

    document.addEventListener('cart:add-failed', function () {
      self.setLoading(false);
      if (self.isOpen) self.fetchCart(false);
    });

    ['cart:updated', 'cart:refresh', 'cart:update', 'cart-update', 'CartUpdate', 'cart:item-added', 'cart:item-removed']
      .forEach(function (evtName) {
        document.addEventListener(evtName, function (e) {
          if (e && e.detail && e.detail.source === 'bb-cart-drawer') return;
          if (!self.isOpen) { self.open(); self.setLoading(true); }
          self.fetchCart(false);
        });
      });
  };

  // Debounced authoritative reconcile. Waits long enough for the underlying
  // /cart/add to have committed server-side, then re-renders from /cart.js.
  CartDrawer.prototype.scheduleReconcile = function () {
    var self = this;
    clearTimeout(self._reconcileTimer);
    self._reconcileTimer = setTimeout(function () { self.fetchCart(false); }, 900);
  };

  CartDrawer.prototype.onCartLinesUpdate = function (event) {
    var self = this;
    var action = event && event.action;

    if (action === 'add') {
      self.open();

      var opt = self.optimisticFromEvent(event);
      if (opt) {
        if (opt.stock != null) {
          window.__bbCartStockSeed = window.__bbCartStockSeed || {};
          window.__bbCartStockSeed[String(opt.variantId)] = opt.stock;
        }
        self.renderOptimistic(opt);
      } else {
        self.setLoading(true);
      }

      if (event.promise && event.promise.then) {
        event.promise
          .then(function (payload) {
            self.setLoading(false);
            var detail = (payload && payload.detail) || {};
            var items = detail.items || [];
            if (items.length) {
              var cart = self.cartFromItems(items);
              self.renderCart(cart);
              self.hydrateSavings(cart);
            } else {
              self.fetchCart(false);
            }
          })
          .catch(function () {
            self.fetchCart(false);
          });
      } else {
        self.fetchCart(false);
      }
      // Reconcile after the add regardless of the promise payload — cartFromItems
      // can under-represent the full cart, so snap to /cart.js as the source of truth.
      self.scheduleReconcile();
    } else if (self.isOpen) {
      self.fetchCart(false);
    }
  };

  CartDrawer.prototype.cartFromItems = function (items) {
    items = items || [];
    var count = 0, total = 0;
    items.forEach(function (i) {
      var qty = i.quantity || 0;
      count += qty;
      total += (i.final_line_price != null) ? i.final_line_price : (i.final_price || 0) * qty;
    });
    return { items: items, item_count: count, total_price: total };
  };

  CartDrawer.prototype.optimisticFromEvent = function (event) {
    var lines = event && event.lines;
    if (!lines || !lines.length) return null;
    var line = lines[0];
    var mid = String(line.merchandiseId || '').split('?')[0];
    var vid = mid.substring(mid.lastIndexOf('/') + 1);
    if (!/^\d+$/.test(vid)) return null;
    var pd = window.__bbCartProduct;
    var v = pd && pd.variants && pd.variants[vid];
    if (!v) return null;
    return {
      variantId: vid,
      size: v.s || '',
      title: v.t || '',
      vendor: v.ven || '',
      image: pdpMainImage() || v.img || '',
      url: v.u || '',
      priceCents: (typeof v.p === 'number') ? v.p : 0,
      compareCents: (typeof v.cap === 'number') ? v.cap : 0,
      stock: (typeof v.q === 'number') ? v.q : null,
      qty: line.quantity || 1
    };
  };

  CartDrawer.prototype.renderOptimistic = function (opt) {
    var addQty = opt.qty || 1;
    var base = (this._lastCart && this._lastCart.items) ? this._lastCart : { items: [] };
    var items = base.items.map(function (i) {
      var c = {}; for (var k in i) c[k] = i[k]; return c;
    });
    var vid = String(opt.variantId);
    var existing = null;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === vid) { existing = items[i]; break; }
    }
    // Cache the compare-at we already know so the reconcile render keeps the
    // strikethrough / "Save" instead of dropping it for a beat (the flicker).
    this._capCache[vid] = (typeof opt.compareCents === 'number') ? opt.compareCents : 0;
    // Lock in the (already on-screen, already-decoded) optimistic image so the
    // reconcile render reuses it instead of swapping to the ajax cart's URL.
    if (opt.image) this._imgCache[vid] = opt.image;

    if (existing) {
      existing.quantity += addQty;
      existing.final_line_price = existing.final_price * existing.quantity;
    } else {
      // Prepend — Shopify returns the newest line first, so inserting at the top
      // matches the reconciled order and avoids a bottom->top jump.
      items.unshift({
        key: 'bb-optimistic-' + vid,
        variant_id: opt.variantId,
        quantity: addQty,
        product_title: opt.title || '',
        vendor: opt.vendor || '',
        variant_title: opt.size || '',
        image: opt.image || '',
        url: opt.url || '',
        final_price: opt.priceCents || 0,
        final_line_price: (opt.priceCents || 0) * addQty,
        compare_at_price: opt.compareCents || 0
      });
    }
    var count = 0, total = 0;
    items.forEach(function (i) {
      count += i.quantity;
      total += (i.final_line_price != null) ? i.final_line_price : (i.final_price || 0) * i.quantity;
    });
    this.renderCart({ items: items, item_count: count, total_price: total, _optimistic: true });
  };

  CartDrawer.prototype.open = function () {
    this.isOpen = true;
    this.panel.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.classList.add('cart-drawer-open-' + this.sid);
  };

  CartDrawer.prototype.close = function () {
    this.isOpen = false;
    this.setLoading(false);
    this.panel.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open-' + this.sid);
  };

  CartDrawer.prototype.setLoading = function (on) {
    if (this.loading) this.loading.classList.toggle('is-active', on);
    if (this.panel) this.panel.classList.toggle('is-loading', on);
  };

  // Slim, on-brand notice shown INSIDE the drawer. Inserted once as a sibling
  // above the item list so a cart re-render (itemsList.innerHTML) never wipes it.
  // Used e.g. when a qty change is rejected because the max available stock is
  // already in the bag. Collapses its own height in/out so nothing else shifts.
  CartDrawer.prototype.toast = function (msg) {
    var el = this._noticeEl;
    if (!el) {
      el = document.createElement('div');
      el.className = 'bb-cd-notice';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = 'box-sizing:border-box;overflow:hidden;max-height:0;opacity:0;margin:0 16px;background:#f6e4df;color:#7a2e1f;border-radius:4px;font-family:Montserrat,sans-serif;font-size:10.5px;font-weight:500;letter-spacing:.09em;text-transform:uppercase;text-align:center;transition:max-height .3s ease,opacity .3s ease,padding .3s ease,margin-bottom .3s ease;';
      if (this.itemsList && this.itemsList.parentNode) {
        this.itemsList.parentNode.insertBefore(el, this.itemsList);
      } else if (this.panel) {
        this.panel.appendChild(el);
      }
      this._noticeEl = el;
    }
    el.textContent = msg;
    void el.offsetWidth;
    el.style.maxHeight = '60px';
    el.style.opacity = '1';
    el.style.padding = '9px 14px';
    el.style.marginTop = '5px';
    el.style.marginBottom = '5px';
    clearTimeout(el._t);
    el._t = setTimeout(function () {
      el.style.maxHeight = '0';
      el.style.opacity = '0';
      el.style.padding = '0 14px';
      el.style.marginTop = '0';
      el.style.marginBottom = '0';
    }, 3600);
  };

  CartDrawer.prototype.fetchAndOpen = function () {
    this.open();
    this.setLoading(true);
    this.fetchCart(false);
  };

  CartDrawer.prototype.fetchCart = function (andOpen) {
    var self = this;
    if (self._fetching) return;
    self._fetching = true;
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self._fetching = false;
        self.setLoading(false);
        self.renderCart(cart);
        if (andOpen !== false) self.open();
        if (cart.items && cart.items.length) self.hydrateSavings(cart);
      })
      .catch(function (err) {
        self._fetching = false;
        self.setLoading(false);
        console.error('[CartDrawer] fetchCart error', err);
      });
  };

  CartDrawer.prototype.hydrateSavings = function (cart) {
    var self = this;
    var unique = cart.items
      .map(function (i) { return i.variant_id; })
      .filter(function (v, i, a) { return a.indexOf(v) === i; })
      .filter(function (v) { return self._capCache[v] == null; });
    if (!unique.length) return;
    var promises = unique.map(function (vid) {
      return fetch('/variants/' + vid + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (v) {
          return { id: vid, cap: (v && v.compare_at_price) ? v.compare_at_price : 0 };
        })
        .catch(function () { return { id: vid, cap: 0 }; });
    });
    Promise.all(promises).then(function (results) {
      var rerender = false;
      results.forEach(function (r) {
        self._capCache[r.id] = r.cap;
        cart.items.forEach(function (item) {
          if (item.variant_id === r.id && r.cap > item.final_price) rerender = true;
        });
      });
      cart.items.forEach(function (item) {
        var cap = self._capCache[item.variant_id];
        if (cap != null) item.compare_at_price = cap;
      });
      if (rerender) self.renderCart(cart);
    });
  };

  CartDrawer.prototype.renderCart = function (cart) {
    var self = this;

    if (!cart._optimistic) this._lastCart = cart;

    if (window.__bbCartStockSeed) {
      for (var vid in window.__bbCartStockSeed) {
        if (self._stockCache[vid] == null) self._stockCache[vid] = window.__bbCartStockSeed[vid];
      }
    }

    if (cart.items) {
      cart.items.forEach(function (item) {
        var cap = self._capCache[item.variant_id];
        if (cap != null && !item.compare_at_price) item.compare_at_price = cap;
      });
    }

    var hasItems = cart.item_count > 0;

    if (this.count) this.count.textContent = cart.item_count || 0;

    this.empty.classList.toggle('is-visible', !hasItems);

    if (this.footer) {
      this.footer.style.display = hasItems ? '' : 'none';
    }

    if (!hasItems) {
      this.itemsList.innerHTML = '';
      this.updateShippingBar(0);
      return;
    }

    this.itemsList.innerHTML = cart.items.map(function (item) {
      return self.renderItem(item);
    }).join('');

    this.itemsList.querySelectorAll('[data-qty-action]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key    = btn.dataset.key;
        var action = btn.dataset.qtyAction;
        var input  = self.itemsList.querySelector('.cart-drawer-qty-val-' + self.sid + '[data-key="' + key + '"]');
        var cur    = parseInt(input.value, 10);
        self.changeQty(key, action === 'inc' ? cur + 1 : Math.max(0, cur - 1));
      });
    });

    this.itemsList.querySelectorAll('[data-remove-key]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.changeQty(btn.dataset.removeKey, 0);
      });
    });

    if (this.subtotal) {
      this.subtotal.textContent = formatMoney(cart.total_price);
    }

    var totalSavings = 0;
    cart.items.forEach(function (item) {
      if (item.compare_at_price && item.compare_at_price > item.final_price) {
        totalSavings += (item.compare_at_price - item.final_price) * item.quantity;
      }
    });

    if (this.savingsTotal) {
      if (totalSavings > 0) {
        this.savingsTotal.textContent = 'You saved ' + formatMoney(totalSavings);
        this.savingsTotal.style.display = 'block';
      } else {
        this.savingsTotal.style.display = 'none';
      }
    }

    this.updateShippingLine(cart.total_price);
    this.updateShippingBar(cart.total_price);
  };

  CartDrawer.prototype.renderItem = function (item) {
    var sid = this.sid;

    var vendorHtml = '';
    if (CONFIG.showVendor && item.vendor) {
      vendorHtml = '<div class="cart-drawer-item-vendor-' + sid + '">' + item.vendor + '</div>';
    }

    var variantHtml = '';
    if (CONFIG.showProductDetails && item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml = '<div class="cart-drawer-item-variant-' + sid + '">' + item.variant_title + '</div>';
    }

    var compareAt = item.compare_at_price;
    var finalUnit = item.final_price;
    var isSale    = compareAt && compareAt > finalUnit;
    var savings   = isSale ? (compareAt - finalUnit) * item.quantity : 0;

    var priceClass = 'cart-drawer-item-price-' + sid + (isSale ? ' is-sale' : '');

    var compareHtml = isSale
      ? '<span class="cart-drawer-item-compare-' + sid + '">' + formatMoney(compareAt) + '</span>'
      : '';

    var savingsHtml = isSale
      ? '<span class="cart-drawer-item-savings-' + sid + '">Save ' + formatMoney(savings) + '</span>'
      : '';

    var priceRowHtml =
      '<div class="cart-drawer-item-price-row-' + sid + '">' +
        '<div class="cart-drawer-item-price-main-' + sid + '">' +
          '<span class="' + priceClass + '">' + formatMoney(finalUnit) + '</span>' +
          compareHtml +
        '</div>' +
        savingsHtml +
      '</div>';

    var stock = this._stockCache ? this._stockCache[item.variant_id] : null;
    var lowStockHtml = (typeof stock === 'number' && stock > 0 && stock <= LOW_STOCK)
      ? '<span class="cart-drawer-item-lowstock-' + sid + '">Only ' + stock + ' left</span>'
      : '';

    var url = item.url || ('/products/' + (item.handle || ''));

    // Reuse the first image we ever showed for this variant. Seed the cache from
    // the ajax image for items that had no optimistic render (e.g. items already
    // in the cart at page load), so every later render keeps the same src.
    var imgUrl = (this._imgCache && this._imgCache[item.variant_id]) || item.image || '';
    if (this._imgCache && imgUrl && this._imgCache[item.variant_id] == null) {
      this._imgCache[item.variant_id] = imgUrl;
    }

    return (
      '<div class="cart-drawer-item-' + sid + '" data-key="' + item.key + '">' +
        '<a class="cart-drawer-item-img-' + sid + '" href="' + url + '">' +
          '<img src="' + imgUrl + '" alt="' + (item.title || item.product_title || '') + '" loading="lazy" width="90" height="116">' +
        '</a>' +
        '<div class="cart-drawer-item-info-' + sid + '">' +
          vendorHtml +
          '<a class="cart-drawer-item-title-' + sid + '" href="' + url + '">' + item.product_title + '</a>' +
          variantHtml +
          priceRowHtml +
          '<div class="cart-drawer-item-footer-' + sid + '">' +
            '<div class="cart-drawer-qty-' + sid + '">' +
              '<button class="cart-drawer-qty-btn-' + sid + '" data-qty-action="dec" data-key="' + item.key + '" aria-label="Decrease quantity">&#8722;</button>' +
              '<input class="cart-drawer-qty-val-' + sid + '" type="number" value="' + item.quantity + '" data-key="' + item.key + '" readonly aria-label="Quantity">' +
              '<button class="cart-drawer-qty-btn-' + sid + '" data-qty-action="inc" data-key="' + item.key + '" aria-label="Increase quantity">&#43;</button>' +
            '</div>' +
            '<button class="cart-drawer-remove-' + sid + '" data-remove-key="' + item.key + '">Remove</button>' +
            lowStockHtml +
          '</div>' +
        '</div>' +
      '</div>'
    );
  };

  CartDrawer.prototype.changeQty = function (key, qty) {
    var self = this;
    if (String(key).indexOf('bb-optimistic-') === 0) return;
    this.setLoading(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) {
      // Keep the HTTP status alongside the parsed body. change.js returns 422
      // (with an error body, NOT a cart) when e.g. the requested quantity exceeds
      // available stock. Passing that body to renderCart would read item_count as
      // undefined and wipe the drawer to "empty" while the real cart is intact.
      return r.json().then(function (body) { return { ok: r.ok, body: body }; });
    })
    .then(function (res) {
      self.setLoading(false);
      if (!res.ok) {
        self.toast('Maximum available quantity is already in your bag');
        self.fetchCart(false); // re-render the true (unchanged) cart, never empty
        return;
      }
      var cart = res.body;
      self.renderCart(cart);
      if (cart.items && cart.items.length) self.hydrateSavings(cart);
      self.notifyHorizon(cart);
    })
    .catch(function (err) {
      self.setLoading(false);
      self.fetchCart(false); // network/parse failure — recover to the real cart
      console.error('[CartDrawer] changeQty error', err);
    });
  };

  CartDrawer.prototype.notifyHorizon = function (cart) {
    var count = cart.item_count || 0;
    var ci = document.querySelector('cart-icon');
    if (ci && typeof ci.renderCartBubble === 'function') {
      ci.renderCartBubble(count, true);
    }
  };

  CartDrawer.prototype.updateShippingLine = function (subtotalCents) {
    var sid  = this.sid;
    var line = this.shippingLine;
    if (CONFIG.isAU) {
      var freeStdAt = (CONFIG.shippingTiers[0] && CONFIG.shippingTiers[0].threshold);
      var freeExpAt = (CONFIG.shippingTiers[1] && CONFIG.shippingTiers[1].threshold);
      var method, isFree;
      if (freeExpAt != null && subtotalCents >= freeExpAt) {
        method = 'Express';  isFree = true;
      } else if (freeStdAt != null && subtotalCents >= freeStdAt) {
        method = 'Standard'; isFree = true;
      } else {
        method = 'Standard'; isFree = false;
      }
      var shipCents = isFree ? 0 : CONFIG.auStandardShipping;
      if (line) {
        var v = isFree
          ? '<span class="cart-drawer-shipping-line-value-' + sid + ' is-free">Complimentary</span>'
          : '<span class="cart-drawer-shipping-line-value-' + sid + '">' + formatMoney(CONFIG.auStandardShipping) + '</span>';
        line.innerHTML = '<span class="cart-drawer-shipping-line-label-' + sid + '">Shipping: ' + method + '</span>' + v;
      }
      if (this.total) this.total.textContent = formatMoney(subtotalCents + shipCents);
    } else if (line) {
      line.innerHTML = '<span class="cart-drawer-shipping-line-label-' + sid + '">Shipping</span>' +
                       '<span class="cart-drawer-shipping-line-value-' + sid + ' is-muted">Calculated at checkout</span>';
    }
  };

  CartDrawer.prototype.updateShippingBar = function (totalCents) {
    if (!this.shippingMsg || !this.shippingFill) return;
    var tiers    = CONFIG.shippingTiers;
    var nextTier = null;

    for (var i = 0; i < tiers.length; i++) {
      if (totalCents >= tiers[i].threshold) {
        // reached this tier; keep scanning for the next unmet one
      } else {
        nextTier = tiers[i];
        break;
      }
    }

    if (!nextTier) {
      var last = tiers[tiers.length - 1];
      this.shippingMsg.textContent = last
        ? (last.reachedMessage || last.message).replace('{amount}', '')
        : '';
      this.shippingFill.style.width = '100%';
    } else {
      var remaining = nextTier.threshold - totalCents;
      var progress  = Math.min((totalCents / nextTier.threshold) * 100, 100);
      this.shippingMsg.textContent  = nextTier.message.replace('{amount}', formatMoney(remaining));
      this.shippingFill.style.width = progress + '%';
    }
  };

  function boot() {
    if (!SID) return;
    if (document.querySelector('#CartDrawer-' + SID)) {
      new CartDrawer(SID);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();